import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, Award, ArrowRightLeft, Minus, ChevronRight, Target, ShieldCheck, Zap, AlertTriangle, Activity, Trophy, Clock } from 'lucide-react';
import { Language, Game } from '../../types';
import { fetchTeamStatistics, fetchTeamTransfers, fetchTeamInfo, fetchTeamRecentMatches } from '../../services/footballService';
import { getTeamName } from '../../services/teamService';
import { t } from '../../i18n';

interface TeamQuickModalProps {
    isOpen: boolean;
    onClose: () => void;
    team: {
        id: string;
        name: string;
        logo: string;
        leagueId?: string;
        season?: string;
    } | null;
    lang: Language;
}

type TeamTab = 'stats' | 'fixtures' | 'transfers';

const CALENDAR_YEAR_LEAGUES = ['1', '292', '253', '98', '71'];

const getCountryCode = (nationality?: string) => {
    if (!nationality) return 'un';
    const map: Record<string, string> = {
        'Spain': 'es', 'England': 'gb-eng', 'Germany': 'de', 'France': 'fr',
        'Italy': 'it', 'Brazil': 'br', 'Argentina': 'ar', 'Portugal': 'pt',
        'Netherlands': 'nl', 'Belgium': 'be', 'Norway': 'no', 'Sweden': 'se',
        'Korea, South': 'kr', 'Japan': 'jp', 'USA': 'us', 'Poland': 'pl',
        'Turkey': 'tr', 'Morocco': 'ma', 'Croatia': 'hr', 'Denmark': 'dk',
        'Egypt': 'eg', 'Senegal': 'sn', 'Uruguay': 'uy', 'Colombia': 'co',
        'Nigeria': 'ng', 'Ghana': 'gh', 'Ivory Coast': 'ci', 'Cameroon': 'cm'
    };
    return map[nationality] || 'un';
};

export const TeamQuickModal: React.FC<TeamQuickModalProps> = ({ isOpen, onClose, team, lang }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [activeTab, setActiveTab] = useState<TeamTab>('stats');
    const [stats, setStats] = useState<any>(null);
    const [teamProfile, setTeamProfile] = useState<any>(null);
    const [transfers, setTransfers] = useState<any[]>([]);
    const [recentMatches, setRecentMatches] = useState<Game[]>([]);
    const [loading, setLoading] = useState(false);
    const [matchesLoading, setMatchesLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsAnimating(true), 10);
            
            if (team?.id) {
                loadTeamData();
            }
            return () => {
                clearTimeout(timer);
            };
        } else {
            setIsAnimating(false);
            setStats(null);
            setTeamProfile(null);
            setTransfers([]);
            setRecentMatches([]);
            setActiveTab('stats');
        }
    }, [isOpen, team?.id]);

    useEffect(() => {
        if (activeTab === 'fixtures' && team?.id && recentMatches.length === 0) {
            loadRecentMatches();
        }
    }, [activeTab, team?.id]);

    const loadTeamData = async () => {
        if (!team?.id) return;
        setLoading(true);
        try {
            const leagueId = team.leagueId || '39'; 
            const season = team.season || new Date().getFullYear().toString();
            
            const [statsData, transfersData, infoData] = await Promise.all([
                fetchTeamStatistics(team.id, leagueId, season),
                fetchTeamTransfers(team.id),
                fetchTeamInfo(team.id)
            ]);
            
            setStats(statsData);
            setTransfers(transfersData || []);
            setTeamProfile(infoData);
        } finally {
            setLoading(false);
        }
    };

    const loadRecentMatches = async () => {
        if (!team?.id) return;
        setMatchesLoading(true);
        try {
            const matches = await fetchTeamRecentMatches(team.id, 15, team.leagueId);
            setRecentMatches(matches);
        } finally {
            setMatchesLoading(false);
        }
    };

    const finishedMatches = useMemo(() => {
        return recentMatches.filter(match => match.status === 'Finished');
    }, [recentMatches]);

    const processedTransfers = useMemo(() => {
        if (!transfers || !team?.id) return [];
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        const flattened: any[] = [];
        
        transfers.forEach(playerData => {
            if (playerData.transfers && Array.isArray(playerData.transfers)) {
                playerData.transfers.forEach((tr: any) => {
                    const dateStr = tr.date;
                    if (!dateStr || !dateRegex.test(dateStr)) return;
                    const trDate = new Date(dateStr);
                    const isIncoming = tr.teams?.in?.id?.toString() === team.id;
                    const isOutgoing = tr.teams?.out?.id?.toString() === team.id;
                    if ((isIncoming || isOutgoing) && trDate >= oneYearAgo) {
                        flattened.push({ player: playerData.player, transfer: tr, dateObj: trDate, isIncoming });
                    }
                });
            }
        });
        return flattened.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
    }, [transfers, team?.id]);

    if (!isOpen || !team) return null;

    const handleClose = () => {
        setIsAnimating(false);
        setTimeout(onClose, 500);
    };

    const handlePlayerClick = (playerId: number) => {
        if (window.navigateTo) {
            window.navigateTo(`/player/${playerId}`);
        } else {
            window.location.hash = `/player/${playerId}`;
        }
        handleClose();
    };

    const handleMatchClick = (matchId: string) => {
        if (window.navigateTo) {
            window.navigateTo(`/game/${matchId}`);
        } else {
            window.location.hash = `/game/${matchId}`;
        }
        handleClose();
    };

    const getMatchResult = (match: Game) => {
        if (!match.score || !team?.id) return null;
        const scores = match.score.split(':').map(Number);
        const isHome = match.homeTeam.id === team.id;
        const teamScore = isHome ? scores[0] : scores[1];
        const opponentScore = isHome ? scores[1] : scores[0];
        if (teamScore > opponentScore) return { label: 'W', color: 'bg-emerald-500' };
        if (teamScore === opponentScore) return { label: 'D', color: 'bg-slate-400' };
        return { label: 'L', color: 'bg-rose-500' };
    };

    const countryName = teamProfile?.team?.country;
    const countryCode = getCountryCode(countryName);

    const goalsFor = stats?.goals?.for?.total?.total || 0;
    const goalsAgainst = stats?.goals?.against?.total?.total || 0;
    const totalGoalsInFixtures = goalsFor + goalsAgainst || 1;

    const isCalendarLeague = team.leagueId && CALENDAR_YEAR_LEAGUES.includes(team.leagueId);

    return (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={handleClose}></div>

            <div className={`relative bg-white w-full sm:max-w-lg rounded-t-[40px] sm:rounded-[32px] shadow-sm flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] h-[85vh] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                
                <div className="absolute top-6 right-6 z-20">
                    <button onClick={handleClose} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all active:scale-90">
                        <X size={20} strokeWidth={3} />
                    </button>
                </div>

                <header className="p-8 pb-4 shrink-0">
                    <div className="flex items-center gap-6 mb-6">
                        <div className="w-20 h-20 rounded-2xl bg-gray-50 border border-gray-100 p-4 flex items-center justify-center shadow-sm">
                            <img src={team.logo} alt={team.name} className="w-full h-full object-contain" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight truncate leading-none mb-2 pr-12">
                                {getTeamName(team.id, lang, team.name)}
                            </h2>
                            <div className="flex items-center gap-3">
                                {countryName && (
                                    <div className="flex items-center gap-1.5">
                                        <img 
                                            src={`https://flagcdn.com/w40/${countryCode}.png`} 
                                            alt={countryName} 
                                            className="w-4 h-3 object-cover rounded-sm"
                                        />
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{countryName}</span>
                                    </div>
                                )}
                                <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                                    {isCalendarLeague ? team.season || '2026' : `Season ${team.season || '25/26'}`}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex bg-slate-100 p-1 rounded-2xl gap-1">
                        {[
                            { id: 'stats', label: t(lang, 'stats') },
                            { id: 'fixtures', label: t(lang, 'fixtures') },
                            { id: 'transfers', label: t(lang, 'topTransfers') }
                        ].map(tab => (
                            <button 
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as TeamTab)}
                                className={`flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 md:hover:text-slate-600'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-8 pt-4">
                    {loading ? (
                        <div className="h-full flex flex-col items-center justify-center gap-4 py-20">
                            <Loader2 className="animate-spin text-rose-500" size={32} />
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">{t(lang, 'syncingIntel')}</span>
                        </div>
                    ) : activeTab === 'stats' && stats ? (
                        <div className="space-y-12 animate-in fade-in duration-500 pb-10">
                            {/* Goals Balance Visualization */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <Zap size={14} className="text-amber-500" />
                                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'goalBalance')}</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-end mb-1">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-emerald-500 uppercase">{t(lang, 'goalsFor')}</span>
                                            <span className="text-2xl font-black text-slate-900 tabular-nums">{goalsFor}</span>
                                        </div>
                                        <div className="flex flex-col text-right">
                                            <span className="text-[10px] font-black text-rose-500 uppercase">{t(lang, 'goalsAgainst')}</span>
                                            <span className="text-2xl font-black text-slate-900 tabular-nums">{goalsAgainst}</span>
                                        </div>
                                    </div>
                                    <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                                        <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${(goalsFor / totalGoalsInFixtures) * 100}%` }} />
                                        <div className="h-full bg-rose-500 transition-all duration-1000" style={{ width: `${(goalsAgainst / totalGoalsInFixtures) * 100}%` }} />
                                    </div>
                                    <div className="flex justify-between text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">
                                        <span>{t(lang, 'offensiveDominance')}</span>
                                        <span>{t(lang, 'defensiveLeakage')}</span>
                                    </div>
                                </div>
                            </section>

                            {/* Key Performance Grid */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <Award size={14} className="text-rose-500" />
                                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'teamDNAIndices')}</h3>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {[
                                        { label: t(lang, 'cleanSheets'), value: stats.clean_sheet?.total || 0, icon: ShieldCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                                        { label: t(lang, 'failedToScore'), value: stats.failed_to_score?.total || 0, icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-50' },
                                        { label: t(lang, 'scoredPenalties'), value: stats.penalty?.scored?.total || 0, icon: Target, color: 'text-indigo-500', bg: 'bg-indigo-50' },
                                        { label: t(lang, 'missedPenalties'), value: stats.penalty?.missed?.total || 0, icon: Minus, color: 'text-slate-400', bg: 'bg-slate-50' },
                                        { label: t(lang, 'bigWins'), value: stats.fixtures?.wins?.total || 0, icon: Zap, color: 'text-amber-500', bg: 'bg-amber-50' },
                                        { label: t(lang, 'toughLosses'), value: stats.fixtures?.loses?.total || 0, icon: AlertTriangle, color: 'text-slate-400', bg: 'bg-slate-50' }
                                    ].map((item, i) => (
                                        <div key={i} className="p-4 rounded-[24px] bg-white border border-slate-100 shadow-sm flex flex-col items-center text-center group md:hover:border-rose-200 transition-all">
                                            <div className={`w-8 h-8 rounded-xl ${item.bg} ${item.color} flex items-center justify-center mb-2.5 transition-transform md:group-hover:scale-110`}>
                                                <item.icon size={16} />
                                            </div>
                                            <span className="block text-[18px] font-black text-slate-900 leading-none mb-1">{item.value}</span>
                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>
                    ) : activeTab === 'fixtures' ? (
                        <div className="space-y-4 animate-in fade-in duration-500 pb-10">
                            {matchesLoading ? (
                                <div className="py-20 flex flex-col items-center justify-center gap-3">
                                    <Loader2 className="animate-spin text-rose-500" />
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{t(lang, 'loadingRecords')}</span>
                                </div>
                            ) : finishedMatches.length > 0 ? (
                                finishedMatches.map((match) => {
                                    const result = getMatchResult(match);
                                    const isHome = match.homeTeam.id === team.id;
                                    const opponent = isHome ? match.awayTeam : match.homeTeam;
                                    
                                    return (
                                        <div 
                                            key={match.id} 
                                            className="p-4 bg-white border border-gray-100 rounded-[24px] flex items-center gap-3 md:hover:shadow-sm transition-all cursor-pointer active:scale-[0.98]"
                                            onClick={() => handleMatchClick(match.id)}
                                        >
                                            <div className="shrink-0 w-10 h-10 p-1.5 bg-slate-50 rounded-xl flex items-center justify-center border border-gray-100">
                                                <img src={opponent.logo} alt="" className="w-full h-full object-contain" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-tighter tabular-nums">{match.dateLabel}</span>
                                                    <div className="w-0.5 h-0.5 rounded-full bg-slate-200"></div>
                                                </div>
                                                <p className="text-xs font-black text-slate-900 truncate uppercase tracking-tight max-w-[140px] sm:max-w-none">
                                                    {getTeamName(opponent.id, lang, opponent.name)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className="text-xs font-black text-slate-900 tabular-nums bg-slate-50 px-2 py-1 rounded-lg">{match.score || '-'}</span>
                                                <div className={`w-7 h-7 rounded-lg ${result?.color || 'bg-slate-100'} flex items-center justify-center text-white text-[10px] font-black shadow-sm`}>
                                                    {result?.label || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="py-20 text-center">
                                    <Clock size={48} className="mx-auto text-gray-100 mb-4" />
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{t(lang, 'noConcludedMatches')}</p>
                                </div>
                            )}
                        </div>
                    ) : activeTab === 'transfers' ? (
                        <div className="space-y-6 animate-in fade-in duration-500">
                            {processedTransfers.length > 0 ? (
                                <div className="divide-y divide-gray-50">
                                    {processedTransfers.map((item, i) => {
                                        const { player, transfer, isIncoming } = item;
                                        return (
                                            <div 
                                                key={i} 
                                                className="py-5 flex items-center justify-between group cursor-pointer active:bg-slate-50 transition-colors"
                                                onClick={() => handlePlayerClick(player.id)}
                                            >
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border-2 ${isIncoming ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-rose-50 border-rose-100 text-rose-600'}`}>
                                                        <span className="text-[10px] font-black">{isIncoming ? 'IN' : 'OUT'}</span>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="text-[15px] font-black text-slate-900 truncate uppercase tracking-tight md:group-hover:text-rose-600 transition-colors">{player?.name}</h4>
                                                        <div className="flex items-center gap-1.5 mt-1">
                                                            <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest truncate">
                                                                {isIncoming 
                                                                    ? <>from{' '}
                                                                        <span className="text-slate-600">
                                                                            {getTeamName(transfer.teams?.out?.id, lang, transfer.teams?.out?.name)}
                                                                        </span>
                                                                        </> 
                                                                    : <>to{' '}
                                                                        <span className="text-slate-600">
                                                                            {getTeamName(transfer.teams?.in?.id, lang, transfer.teams?.in?.name)}
                                                                        </span>
                                                                        </>
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <div className="text-right">
                                                        <span className="text-[10px] font-black text-slate-900 tabular-nums block">{transfer.date}</span>
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">{t(lang, transfer.type?.toLowerCase()) || transfer.type || t(lang, 'transfer')}</span>
                                                    </div>
                                                    <ChevronRight size={16} className="text-slate-300 md:group-hover:text-rose-500" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="py-20 text-center">
                                    <ArrowRightLeft size={48} className="mx-auto text-gray-100 mb-4" />
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{t(lang, 'noVerifiedTransfers')}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="py-20 text-center">
                            <Minus size={48} className="mx-auto text-gray-100 mb-4" />
                            <p className="text-sm font-black text-slate-300 uppercase tracking-widest">{t(lang, 'noStatsSynced')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
